(() => {
  const ROOT = '[data-bw-colour-showcase]';

  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
  const mod = (n, m) => ((n % m) + m) % m;

  function hexToRgb(hex) {
    const clean = String(hex || '#334fb4').trim().replace('#', '');
    const full = clean.length === 3 ? clean.split('').map((x) => x + x).join('') : clean.padEnd(6, '0').slice(0, 6);
    return [parseInt(full.slice(0, 2), 16) / 255, parseInt(full.slice(2, 4), 16) / 255, parseInt(full.slice(4, 6), 16) / 255];
  }

  function initProgram(canvas, fragmentSource) {
    const gl = canvas.getContext('webgl', { alpha: true, antialias: false, premultipliedAlpha: true });
    if (!gl) return null;
    const vertexSource = `attribute vec2 a_position; void main(){ gl_Position=vec4(a_position,0.0,1.0); }`;
    const compile = (type, source) => {
      const shader = gl.createShader(type);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        gl.deleteShader(shader);
        return null;
      }
      return shader;
    };
    const vs = compile(gl.VERTEX_SHADER, vertexSource);
    const fs = compile(gl.FRAGMENT_SHADER, fragmentSource);
    if (!vs || !fs) return null;
    const program = gl.createProgram();
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return null;
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1,1,-1,-1,1,-1,1,1,-1,1,1]), gl.STATIC_DRAW);
    gl.useProgram(program);
    const pos = gl.getAttribLocation(program, 'a_position');
    gl.enableVertexAttribArray(pos);
    gl.vertexAttribPointer(pos, 2, gl.FLOAT, false, 0, 0);
    return { gl, program };
  }

  const shineFragment = `
    precision mediump float;
    uniform vec2 u_resolution;
    uniform vec2 u_mouse;
    uniform float u_time;
    uniform float u_density;
    uniform float u_sparkle;
    uniform vec3 u_base;
    uniform vec3 u_highlight;
    float hash(vec2 p){ return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453); }
    void main(){
      vec2 uv=gl_FragCoord.xy/u_resolution.xy;
      vec2 aspect=vec2(u_resolution.x/u_resolution.y,1.0);
      vec2 p=uv*aspect;
      float scale=max(12.0,u_density);
      vec2 g=p*scale;
      float row=floor(g.y);
      g.x+=mod(row,2.0)*0.5;
      vec2 id=floor(g);
      vec2 f=fract(g)-0.5;
      f.y*=1.12;
      float d=length(f);
      float dotMask=smoothstep(0.39,0.315,d);
      float wave=0.5+0.5*sin(id.x*0.53+id.y*0.78+u_time*2.1+sin(id.y*0.31+u_time));
      vec2 m=u_mouse*aspect;
      float md=length(p-m);
      float mouseGlow=exp(-md*5.6);
      float rand=hash(id);
      float sparkle=pow(max(0.0,wave),8.0)*rand*u_sparkle;
      vec3 metal=mix(u_base,u_highlight,0.18+wave*0.42+mouseGlow*0.55+sparkle);
      float sphere=smoothstep(0.43,0.0,d);
      metal*=0.52+sphere*0.72;
      vec3 bg=mix(vec3(0.025,0.032,0.043),u_base*0.12,0.38);
      vec3 col=mix(bg,metal,dotMask);
      col+=u_highlight*mouseGlow*dotMask*0.22;
      gl_FragColor=vec4(col,1.0);
    }
  `;

  const smokeFragment = `
    precision mediump float;
    uniform vec2 u_resolution;
    uniform float u_time;
    uniform vec3 u_color;
    float hash(vec2 p){ return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453123); }
    float noise(vec2 p){
      vec2 i=floor(p),f=fract(p); f=f*f*(3.0-2.0*f);
      return mix(mix(hash(i),hash(i+vec2(1.0,0.0)),f.x),mix(hash(i+vec2(0.0,1.0)),hash(i+vec2(1.0,1.0)),f.x),f.y);
    }
    mat2 rot(float a){ float c=cos(a),s=sin(a); return mat2(c,-s,s,c); }
    float fbm(vec2 p){
      float v=0.0,a=0.5; mat2 r=rot(0.47);
      for(int i=0;i<6;i++){ v+=a*noise(p); p=r*p*2.03+vec2(9.7,3.1); a*=0.5; }
      return v;
    }
    void main(){
      vec2 uv=(gl_FragCoord.xy-0.5*u_resolution.xy)/u_resolution.y;
      float rad=length(uv);
      float angle=atan(uv.y,uv.x)+(1.0-smoothstep(0.0,0.9,rad))*(1.65+u_time*0.16);
      vec2 p=vec2(cos(angle),sin(angle))*rad*3.25;
      vec2 q=vec2(fbm(p+vec2(0.0,u_time*0.05)),fbm(p+vec2(5.2,1.3)-u_time*0.04));
      vec2 r=vec2(fbm(p+2.2*q+vec2(1.7,9.2)),fbm(p+2.0*q+vec2(8.3,2.8)));
      float n=fbm(p+3.0*r+q*1.5);
      float column=exp(-pow(rad*1.18,2.0)*2.2);
      float filament=smoothstep(0.43,0.83,n)*column;
      float wisps=smoothstep(0.34,0.72,n)*column*0.42;
      float alpha=clamp(filament+wisps,0.0,0.72);
      vec3 col=mix(u_color*0.36,u_color,clamp(n*1.05,0.0,1.0));
      gl_FragColor=vec4(col,alpha);
    }
  `;

  function createRenderer(canvas, fragment, uniforms) {
    const state = initProgram(canvas, fragment);
    if (!state) return null;
    const { gl, program } = state;
    const locations = {};
    Object.keys(uniforms).forEach((key) => { locations[key] = gl.getUniformLocation(program, key); });
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      const width = Math.max(2, Math.round(rect.width * dpr));
      const height = Math.max(2, Math.round(rect.height * dpr));
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
        gl.viewport(0, 0, width, height);
      }
    };
    const ro = 'ResizeObserver' in window ? new ResizeObserver(resize) : null;
    if (ro) ro.observe(canvas);
    resize();
    return { gl, program, locations, resize, disconnect: () => ro && ro.disconnect() };
  }

  function initRoot(root) {
    if (!root || root.dataset.bwInitialised === 'true') return;
    root.dataset.bwInitialised = 'true';

    const buttons = [...root.querySelectorAll('[data-bw-colour-button]')];
    if (!buttons.length) return;
    const preview = root.querySelector('[data-bw-preview]');
    const previewSurface = root.querySelector('[data-bw-preview-surface]');
    const previewImage = root.querySelector('[data-bw-preview-image]');
    const activeName = root.querySelector('[data-bw-active-name]');
    const activeReference = root.querySelector('[data-bw-active-reference]');
    const activeFinish = root.querySelector('[data-bw-active-finish]');
    const activeNote = root.querySelector('[data-bw-active-note]');
    const shineName = root.querySelector('[data-bw-shine-name]');
    const shineFinish = root.querySelector('[data-bw-shine-finish]');
    const shineCard = root.querySelector('[data-bw-shine-card]');
    const shineCanvas = root.querySelector('[data-bw-shine]');
    const shineFallback = root.querySelector('[data-bw-shine-fallback]');
    const smokeCanvas = root.querySelector('[data-bw-smoke]');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const shadersEnabled = root.dataset.shaders === 'true';
    let activeIndex = 0;
    let activeColor = hexToRgb(buttons[0].dataset.color);
    let activeHighlight = hexToRgb(buttons[0].dataset.highlight);
    let smokeColor = [...activeColor];
    let smokeTarget = [...activeColor];
    let mouse = [0.5, 0.5];
    let hoverSpeed = 0;
    let hoverSpeedTarget = 0;
    let shineTime = 0;
    let smokeTime = 0;
    let lastFrame = performance.now();
    let raf = 0;

    const radiusBase = Number(root.dataset.radius || 240);
    const gapDegrees = Number(root.dataset.gap || 34);
    const visibleCount = Math.max(3, Number(root.dataset.visibleCount || 7));
    const halfVisible = Math.floor(visibleCount / 2);

    const positionButtons = () => {
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const radius = mobile ? Math.min(radiusBase * 0.72, 165) : radiusBase;
      const yFactor = mobile ? 0.5 : 0.58;
      buttons.forEach((button, index) => {
        let d = index - activeIndex;
        const n = buttons.length;
        if (d > n / 2) d -= n;
        if (d < -n / 2) d += n;
        const abs = Math.abs(d);
        const angle = d * gapDegrees * Math.PI / 180;
        const x = Math.sin(angle) * radius;
        const y = Math.cos(angle) * radius * yFactor;
        const visible = abs <= halfVisible;
        const scale = visible ? Math.max(.58, 1 - abs * .105) : .48;
        const opacity = visible ? Math.max(.12, 1 - abs * .23) : 0;
        button.style.setProperty('--orbit-x', `${x}px`);
        button.style.setProperty('--orbit-y', `${y}px`);
        button.style.setProperty('--orbit-scale', scale.toFixed(3));
        button.style.setProperty('--orbit-opacity', opacity.toFixed(3));
        button.setAttribute('aria-hidden', visible ? 'false' : 'true');
      });
    };

    const setSmokeTarget = (hex) => { smokeTarget = hexToRgb(hex); };

    const select = (index, focus = false) => {
      activeIndex = mod(index, buttons.length);
      const button = buttons[activeIndex];
      buttons.forEach((b, i) => {
        const on = i === activeIndex;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
      });
      preview.classList.add('is-changing');
      window.setTimeout(() => preview.classList.remove('is-changing'), reduced ? 0 : 280);

      const color = button.dataset.color || '#334fb4';
      const highlight = button.dataset.highlight || color;
      activeColor = hexToRgb(color);
      activeHighlight = hexToRgb(highlight);
      setSmokeTarget(color);
      previewSurface.style.setProperty('--active-color', color);
      previewSurface.style.setProperty('--active-highlight', highlight);
      activeName.textContent = button.dataset.name || '';
      activeReference.textContent = button.dataset.reference || '';
      activeFinish.textContent = button.dataset.finish || '';
      activeNote.textContent = button.dataset.note || '';
      shineName.textContent = button.dataset.name || '';
      shineFinish.textContent = button.dataset.finish || '';
      shineFallback.style.backgroundColor = color;
      const src = button.dataset.image || '';
      if (src) {
        previewImage.src = src;
        previewImage.alt = button.dataset.name || '';
        previewImage.classList.add('has-image');
      } else {
        previewImage.removeAttribute('src');
        previewImage.alt = '';
        previewImage.classList.remove('has-image');
      }
      positionButtons();
      if (focus) button.focus({ preventScroll: true });
    };

    buttons.forEach((button, index) => {
      button.addEventListener('click', () => select(index));
      button.addEventListener('mouseenter', () => setSmokeTarget(button.dataset.color));
      button.addEventListener('mouseleave', () => setSmokeTarget(buttons[activeIndex].dataset.color));
      button.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); select(activeIndex - 1, true); }
        if (event.key === 'ArrowRight') { event.preventDefault(); select(activeIndex + 1, true); }
        if (event.key === 'Home') { event.preventDefault(); select(0, true); }
        if (event.key === 'End') { event.preventDefault(); select(buttons.length - 1, true); }
      });
    });

    root.addEventListener('pointerenter', () => { hoverSpeedTarget = reduced ? 0 : 1; });
    root.addEventListener('pointerleave', () => { hoverSpeedTarget = 0; setSmokeTarget(buttons[activeIndex].dataset.color); });

    if (shineCard) {
      shineCard.addEventListener('pointermove', (event) => {
        const rect = shineCard.getBoundingClientRect();
        const x = clamp((event.clientX - rect.left) / rect.width, 0, 1);
        const y = clamp((event.clientY - rect.top) / rect.height, 0, 1);
        mouse = [x, 1 - y];
        shineCard.style.setProperty('--mx', `${x * 100}%`);
        shineCard.style.setProperty('--my', `${y * 100}%`);
        if (!reduced) {
          const rx = (0.5 - y) * 7;
          const ry = (x - 0.5) * 8;
          shineCard.style.transform = `perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg) translateY(-5px)`;
        }
      });
      shineCard.addEventListener('pointerleave', () => {
        mouse = [0.5, 0.5];
        shineCard.style.transform = '';
      });
    }

    let shineRenderer = null;
    let smokeRenderer = null;
    if (shadersEnabled && !reduced) {
      shineRenderer = createRenderer(shineCanvas, shineFragment, { u_resolution:1,u_mouse:1,u_time:1,u_density:1,u_sparkle:1,u_base:1,u_highlight:1 });
      smokeRenderer = createRenderer(smokeCanvas, smokeFragment, { u_resolution:1,u_time:1,u_color:1 });
    }
    if (shineRenderer) shineFallback.style.display = 'none';
    if (!smokeRenderer && smokeCanvas) smokeCanvas.style.display = 'none';

    const draw = (now) => {
      const dt = Math.min(0.05, (now - lastFrame) / 1000);
      lastFrame = now;
      hoverSpeed += (hoverSpeedTarget - hoverSpeed) * Math.min(1, dt * 3.2);
      shineTime += dt * (Number(root.dataset.waveSpeed || 38) / 38);
      smokeTime += dt * hoverSpeed;
      for (let i = 0; i < 3; i++) smokeColor[i] += (smokeTarget[i] - smokeColor[i]) * Math.min(1, dt * 3.8);

      if (shineRenderer) {
        const { gl, program, locations } = shineRenderer;
        shineRenderer.resize();
        gl.useProgram(program);
        gl.uniform2f(locations.u_resolution, shineCanvas.width, shineCanvas.height);
        gl.uniform2f(locations.u_mouse, mouse[0], mouse[1]);
        gl.uniform1f(locations.u_time, shineTime);
        gl.uniform1f(locations.u_density, Number(root.dataset.dotDensity || 36));
        gl.uniform1f(locations.u_sparkle, Number(root.dataset.sparkle || 55) / 45);
        gl.uniform3f(locations.u_base, activeColor[0], activeColor[1], activeColor[2]);
        gl.uniform3f(locations.u_highlight, activeHighlight[0], activeHighlight[1], activeHighlight[2]);
        gl.drawArrays(gl.TRIANGLES, 0, 6);
      }
      if (smokeRenderer) {
        const { gl, program, locations } = smokeRenderer;
        smokeRenderer.resize();
        gl.useProgram(program);
        gl.uniform2f(locations.u_resolution, smokeCanvas.width, smokeCanvas.height);
        gl.uniform1f(locations.u_time, smokeTime);
        gl.uniform3f(locations.u_color, smokeColor[0], smokeColor[1], smokeColor[2]);
        gl.drawArrays(gl.TRIANGLES, 0, 6);
      }
      raf = requestAnimationFrame(draw);
    };
    if (shineRenderer || smokeRenderer) raf = requestAnimationFrame(draw);

    const heading = root.querySelector('[data-bw-sweep-heading]');
    if (heading && !reduced) {
      const text = heading.textContent;
      heading.setAttribute('aria-label', text);
      heading.textContent = '';
      [...text].forEach((char, index) => {
        const span = document.createElement('span');
        span.className = 'bw-cs__char';
        span.style.setProperty('--char-index', index);
        span.setAttribute('aria-hidden', 'true');
        span.textContent = char === ' ' ? '\u00a0' : char;
        heading.appendChild(span);
      });
      root.classList.add('is-sweep-ready');
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            root.classList.add('is-sweep-visible');
            observer.disconnect();
          }
        }, { threshold: 0.35 });
        observer.observe(heading);
      } else root.classList.add('is-sweep-visible');
    }

    const onResize = () => positionButtons();
    window.addEventListener('resize', onResize, { passive: true });
    select(0);

    root._bwDestroy = () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      if (shineRenderer) shineRenderer.disconnect();
      if (smokeRenderer) smokeRenderer.disconnect();
    };
  }

  const initAll = (scope = document) => scope.querySelectorAll(ROOT).forEach(initRoot);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches && event.target.matches(ROOT) ? event.target : event.target.querySelector && event.target.querySelector(ROOT);
    if (root && root._bwDestroy) root._bwDestroy();
  });
})();
